import { useState, useEffect } from 'react';
import { Droplets, CheckCircle, AlertTriangle, ChevronLeft, ChevronRight, List, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PhotoCapture } from './PhotoCapture';
import type { PhotoGeoMap } from '../lib/photoLocation';
import { SearchableSelect } from './SearchableSelect';
import { PHOTO_BUCKETS } from '../lib/photoUpload';
import { InspectionProgressBar, InspectionQuickNav, ItemStatus } from './InspectionProgressBar';
import { logEvent } from '../lib/telemetry';

interface WorkCodeTranslation {
  work_code_id: string;
  locale: string;
  translated_code: string;
  translated_description: string;
}

interface WorkCode {
  id?: string;
  code: string;
  description: string;
  is_default: boolean;
  is_issue: boolean;
}

interface Zone {
  id: string;
  zone_number: number;
  type: string;
  location: string;
}

interface Controller {
  id: string;
  name: string;
  controller_number: number;
  location: string;
  zones: Zone[];
}

interface ZoneIssueEntry {
  tempId: string;
  work_code: string;
  notes: string;
  photo_urls: string[];
  /** Where each photo was taken, keyed by its URL (or offline marker). */
  photo_geo?: PhotoGeoMap;
}

interface ZoneInspection {
  zone_id: string;
  issues: ZoneIssueEntry[];
}

interface FlatZone {
  zone: Zone;
  controllerNumber: number;
  controllerId: string;
}

interface ZonesInspectionFormProps {
  controllers: Controller[];
  inspections: ZoneInspection[];
  workCodes: WorkCode[];
  onInspectionsChange: (inspections: ZoneInspection[]) => void;
  expandedControllers?: Set<string>;
  onExpandedControllersChange?: (expanded: Set<string>) => void;
  expandedZones?: Set<string>;
  onExpandedZonesChange?: (expanded: Set<string>) => void;
  translations?: WorkCodeTranslation[];
  localDraftId?: string;
}

export function ZonesInspectionForm({
  controllers,
  inspections,
  workCodes,
  onInspectionsChange,
  translations = [],
  localDraftId,
}: ZonesInspectionFormProps) {
  const { t } = useTranslation('inspection');
  const [localInspections, setLocalInspections] = useState<ZoneInspection[]>(inspections);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showQuickNav, setShowQuickNav] = useState(false);

  const flatZones: FlatZone[] = controllers.flatMap(c =>
    c.zones.map(z => ({
      zone: z,
      controllerNumber: c.controller_number,
      controllerId: c.id,
    }))
  );

  useEffect(() => {
    const allZones = controllers.flatMap(c => c.zones);
    setLocalInspections(prev => {
      return allZones.map(zone => {
        const existing = prev.find(i => i.zone_id === zone.id);
        if (existing) return existing;
        return {
          zone_id: zone.id,
          issues: [{
            tempId: `issue-${zone.id}-${Date.now()}`,
            work_code: '',
            notes: '',
            photo_urls: [],
          }],
        };
      });
    });
  }, [controllers, workCodes]);

  const getStatus = (zoneId: string): ItemStatus => {
    const inspection = localInspections.find(i => i.zone_id === zoneId);
    if (!inspection || inspection.issues.length === 0) return 'pending';
    const allSelected = inspection.issues.every(issue => issue.work_code !== '');
    if (!allSelected) return 'pending';
    const hasIssue = inspection.issues.some(issue => {
      const wc = workCodes.find(w => w.code === issue.work_code);
      return wc?.is_issue;
    });
    return hasIssue ? 'issues' : 'good';
  };

  const findFlatZone = (zoneId: string) => flatZones.find(fz => fz.zone.id === zoneId);

  const handleAddIssue = (zoneId: string) => {
    const fz = findFlatZone(zoneId);
    const existing = localInspections.find(i => i.zone_id === zoneId);
    void logEvent('inspection_zone_issue_added', `+ issue on zone ${fz?.zone.zone_number ?? '?'}`, {
      metadata: {
        zone_id: zoneId,
        zone_number: fz?.zone.zone_number ?? null,
        controller_id: fz?.controllerId ?? null,
        controller_number: fz?.controllerNumber ?? null,
        count_after: (existing?.issues.length ?? 0) + 1,
      },
    });
    const newIssue: ZoneIssueEntry = {
      tempId: `issue-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      work_code: '',
      notes: '',
      photo_urls: [],
    };
    const updated = localInspections.map(insp =>
      insp.zone_id === zoneId
        ? { ...insp, issues: [...insp.issues, newIssue] }
        : insp
    );
    setLocalInspections(updated);
    onInspectionsChange(updated);
  };

  const handleRemoveIssue = (zoneId: string, issueIndex: number) => {
    const fz = findFlatZone(zoneId);
    const existing = localInspections.find(i => i.zone_id === zoneId);
    const removed = existing?.issues[issueIndex];
    void logEvent('inspection_zone_issue_removed', `- issue on zone ${fz?.zone.zone_number ?? '?'}`, {
      metadata: {
        zone_id: zoneId,
        zone_number: fz?.zone.zone_number ?? null,
        controller_id: fz?.controllerId ?? null,
        removed_work_code: removed?.work_code || null,
        count_after: Math.max((existing?.issues.length ?? 0) - 1, 0),
      },
    });
    const updated = localInspections.map(insp =>
      insp.zone_id === zoneId
        ? { ...insp, issues: insp.issues.filter((_, i) => i !== issueIndex) }
        : insp
    );
    setLocalInspections(updated);
    onInspectionsChange(updated);
  };

  const handleIssueFieldChange = (zoneId: string, issueIndex: number, field: keyof ZoneIssueEntry, value: any) => {
    if (field === 'work_code') {
      const fz = findFlatZone(zoneId);
      const wc = workCodes.find(w => w.code === value);
      void logEvent('inspection_zone_work_code_changed', `Zone ${fz?.zone.zone_number ?? '?'} -> ${value}`, {
        metadata: {
          zone_id: zoneId,
          zone_number: fz?.zone.zone_number ?? null,
          controller_id: fz?.controllerId ?? null,
          issue_index: issueIndex,
          work_code: value,
          is_issue: wc?.is_issue ?? null,
        },
      });
    }
    const updated = localInspections.map(insp => {
      if (insp.zone_id !== zoneId) return insp;
      const newIssues = [...insp.issues];
      newIssues[issueIndex] = { ...newIssues[issueIndex], [field]: value };
      return { ...insp, issues: newIssues };
    });
    setLocalInspections(updated);
    onInspectionsChange(updated);
  };

  const handlePhotoGeoChange = (zoneId: string, issueIndex: number, geo: PhotoGeoMap) => {
    const updated = localInspections.map(insp => {
      if (insp.zone_id !== zoneId) return insp;
      const newIssues = [...insp.issues];
      newIssues[issueIndex] = { ...newIssues[issueIndex], photo_geo: geo };
      return { ...insp, issues: newIssues };
    });
    setLocalInspections(updated);
    onInspectionsChange(updated);
  };

  const handlePhotosChange = (zoneId: string, issueIndex: number, urls: string[]) => {
    const updated = localInspections.map(insp => {
      if (insp.zone_id !== zoneId) return insp;
      const newIssues = [...insp.issues];
      newIssues[issueIndex] = { ...newIssues[issueIndex], photo_urls: urls };
      return { ...insp, issues: newIssues };
    });
    setLocalInspections(updated);
    onInspectionsChange(updated);
  };

  const goNext = () => {
    if (currentIndex < flatZones.length - 1) setCurrentIndex(currentIndex + 1);
  };

  const goPrev = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const totalZones = flatZones.length;

  if (controllers.length === 0 || totalZones === 0) {
    return (
      <div className="bg-surface rounded-xl shadow-sm border border-line p-8 text-center">
        <Droplets size={48} className="mx-auto text-theme-secondary mb-4" />
        <h3 className="text-lg font-semibold text-theme-secondary mb-2">{t('zoneForm.noZones')}</h3>
        <p className="text-theme-secondary">{t('zoneForm.noZonesHint')}</p>
      </div>
    );
  }

  const current = flatZones[currentIndex];
  const inspection = localInspections.find(i => i.zone_id === current.zone.id);
  const status = getStatus(current.zone.id);

  const progressItems = flatZones.map(fz => ({
    id: fz.zone.id,
    label: `C#${fz.controllerNumber} Z#${fz.zone.zone_number} - ${fz.zone.type}`,
    status: getStatus(fz.zone.id),
  }));

  const getTranslatedCode = (wc: WorkCode) => {
    if (!wc.id || translations.length === 0) return wc.code;
    const tr = translations.find(x => x.work_code_id === wc.id);
    return tr?.translated_code || wc.code;
  };

  const issueWorkCodes = workCodes.filter(wc => wc.is_issue);
  const nonIssueWorkCodes = workCodes.filter(wc => !wc.is_issue);
  const sortedWorkCodes = [...nonIssueWorkCodes, ...issueWorkCodes];

  return (
    <div className="space-y-3">
      <button onClick={() => setShowQuickNav(true)} className="w-full">
        <InspectionProgressBar
          items={progressItems}
          currentIndex={currentIndex}
          onItemTap={setCurrentIndex}
        />
      </button>

      <div className="bg-surface rounded-xl shadow-sm border border-line overflow-hidden">
        <div className="bg-subtle border-b border-line px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Droplets size={18} className="text-blue-500 flex-shrink-0" />
              <div className="min-w-0">
                <h3 className="font-semibold text-theme-primary text-sm truncate">
                  C#{current.controllerNumber} - Zone #{current.zone.zone_number}
                </h3>
                <p className="text-xs text-theme-secondary truncate capitalize">
                  {current.zone.type}{current.zone.location && ` - ${current.zone.location}`}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 ml-2">
              {status === 'good' ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 rounded-md text-xs font-medium">
                  <CheckCircle size={14} /> {t('status.ok')}
                </span>
              ) : status === 'issues' ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 rounded-md text-xs font-medium">
                  <AlertTriangle size={14} /> {t('status.issues')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-subtle text-theme-secondary rounded-md text-xs font-medium">
                  <AlertTriangle size={14} /> {t('status.pending')}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-3 space-y-2">
          {inspection?.issues.map((issue, issueIndex) => {
            const selectedWc = workCodes.find(w => w.code === issue.work_code);
            const isIssueType = selectedWc?.is_issue;

            return (
              <div
                key={issue.tempId}
                className={`rounded-lg border p-3 transition-colors ${
                  isIssueType ? 'border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10' : 'border-line bg-subtle'
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-2">
                    <SearchableSelect
                      value={issue.work_code}
                      onChange={(v) => handleIssueFieldChange(current.zone.id, issueIndex, 'work_code', v)}
                      options={sortedWorkCodes.map((wc) => ({
                        value: wc.code,
                        label: `${getTranslatedCode(wc)}${wc.is_issue ? ` ${t('zoneForm.issueSuffix')}` : ''}`,
                        searchText: `${wc.code} ${getTranslatedCode(wc)}`,
                      }))}
                      placeholder={t('zoneForm.selectWorkCode')}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
                        issue.work_code === '' ? 'border-amber-400 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300' : 'border-line bg-surface text-theme-primary'
                      }`}
                    />

                    {isIssueType && (
                      <>
                        <textarea
                          value={issue.notes}
                          onChange={(e) => handleIssueFieldChange(current.zone.id, issueIndex, 'notes', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-line rounded-lg bg-surface text-theme-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                          placeholder={t('zoneForm.describeIssuePlaceholder')}
                        />

                        <PhotoCapture
                          photoUrls={issue.photo_urls}
                          bucket={PHOTO_BUCKETS.INSPECTION}
                          pathPrefix={`zone_${current.zone.id}_issue${issueIndex}`}
                          onPhotosChange={(urls) => handlePhotosChange(current.zone.id, issueIndex, urls)}
                          photoGeo={issue.photo_geo}
                          onPhotoGeoChange={(geo) => handlePhotoGeoChange(current.zone.id, issueIndex, geo)}
                          localDraftId={localDraftId}
                        />
                      </>
                    )}
                  </div>

                  {(inspection?.issues.length || 0) > 1 && (
                    <button
                      onClick={() => handleRemoveIssue(current.zone.id, issueIndex)}
                      className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <button
            onClick={() => handleAddIssue(current.zone.id)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-line text-theme-secondary rounded-lg hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-blue-500/10 transition-colors text-sm font-medium active:scale-[0.98]"
          >
            <Plus size={16} />
            {t('zoneForm.addWorkCode')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={goPrev}
          disabled={currentIndex === 0}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-surface border border-line rounded-xl text-theme-primary font-medium disabled:opacity-30 disabled:cursor-not-allowed active:bg-subtle transition-colors"
        >
          <ChevronLeft size={20} />
          <span>{t('nav.prev')}</span>
        </button>
        <button
          onClick={() => setShowQuickNav(true)}
          className="p-3.5 bg-surface border border-line rounded-xl text-theme-secondary active:bg-subtle transition-colors"
          title={t('nav.jumpToZoneTitle')}
        >
          <List size={20} />
        </button>
        <button
          onClick={goNext}
          disabled={currentIndex === flatZones.length - 1}
          className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-blue-600 text-white rounded-xl font-medium disabled:opacity-30 disabled:cursor-not-allowed active:bg-blue-700 transition-colors"
        >
          <span>{t('nav.next')}</span>
          <ChevronRight size={20} />
        </button>
      </div>

      <InspectionQuickNav
        items={progressItems}
        currentIndex={currentIndex}
        onSelect={setCurrentIndex}
        isOpen={showQuickNav}
        onClose={() => setShowQuickNav(false)}
        title={t('nav.jumpToZone')}
      />
    </div>
  );
}
